import AppBar from '@material-ui/core/AppBar';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { Theme } from '@material-ui/core/styles/createMuiTheme';
import TextField from '@material-ui/core/TextField';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import { push } from 'connected-react-router';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { initializeStore as initializeStoreAction } from '../actions/common';
import { IMessage, showMessage as showMessageAction } from '../actions/message';
import MessageDialog from '../components/MessageDialog';
import Progress from '../components/Progress';
import { IRootState } from '../reducers';
import i18n from '../resources/i18n';
import * as scenario from '../scenario/login';

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		appBar: {
			position: 'relative',
			width: 'auto',
		},
		careerDialog: {
			width: '80%',
		},
		form: {
			margin: 'auto',
			maxWidth: 700,
		},
		root: {
			flexGrow: 1,
			padding: theme.spacing(4),
		},
		title: {
			fontSize: 14,
			marginBottom: 16,
		},
	})
);

export interface IPwrPasswordChangeProps {
	/**
	 * 認証キー
	 */
	authKey: string;

	/**
	 * 法人コード
	 */
	companyCode?: string;
}

interface IProps
	extends IPwrPasswordChangeProps,
		ReturnType<typeof mapStateToProps>,
		ReturnType<typeof mapDispatchToProps> {}

/**
 * パスワード変更ハンドラ
 * @param setState
 * @param event
 */
const createChangeHandler = (
	setState: React.Dispatch<React.SetStateAction<string>>
) => (event: React.ChangeEvent<HTMLInputElement>) => {
	setState(event.target.value);
};

/**
 * キー押下ハンドラ
 * @param event
 */
const createKeyPressHandler = (changePassword: () => void) => (
	event: React.KeyboardEvent<HTMLDivElement>
) => {
	if (event.key === 'Enter') {
		changePassword();
	}
};

/**
 * パスワード検証
 * @param event
 */
const verifyPassword = (newPassword: string, reInputPassword: string) => {
	const messages: IMessage[] = [];
	if (!newPassword && !reInputPassword) {
		// 両方入力されていない
		messages.push({
			content: 'COM_E_0005',
			replacements: [i18n.t('PwrPasswordChange_0001')],
		});
	} else {
		if (!newPassword) {
			// 変更後パスワードのみ入力されていない
			messages.push({
				content: 'COM_E_0005',
				replacements: [i18n.t('PwrPasswordChange_0002')],
			});
		} else if (!reInputPassword) {
			// 変更後パスワード再入力のみ入力されていない
			messages.push({
				content: 'COM_E_0005',
				replacements: [i18n.t('PwrPasswordChange_0003')],
			});
		}

		if (newPassword.length > 256) {
			// 最大桁数チェック
			messages.push({
				content: 'COM_E_0006',
				replacements: [i18n.t('PwrPasswordChange_0004'), '256'],
			});
		}
		// 入力許可文字列
		const allowvalue =
			"0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ-_.`!@#$%&'*+/=?^{|}";
		for (const val of newPassword) {
			if (allowvalue.indexOf(val) < 0) {
				// 差異チェック
				messages.push({
					content: 'CHK_E_0033',
					replacements: [i18n.t('PwrPasswordChange_0005')],
				});
				break;
			}
		}
		if (newPassword !== reInputPassword) {
			// 差異チェック
			messages.push({
				content: 'COM_E_0007',
				replacements: [
					i18n.t('PwrPasswordChange_0006'),
					i18n.t('PwrPasswordChange_0007'),
				],
			});
		}
	}
	return messages;
};

/**
 * メッセージクローズハンドラ
 */
const createCloseMessageDialogHandler = (
	showMessage: IProps['showMessage']
) => () => {
	showMessage([]);
};

/**
 * パスワード変更画面
 */
const PwrChangePassword: React.FC<IProps> = ({
	authKey,
	changePassword,
	companyCode,
	initializeStore,
	isShowProgress,
	loginId,
	loginState: { pwr },
	message,
	pwrPasswordChangeInit,
	showMessage,
}) => {
	const classes = useStyles();

	useEffect(
		() => {
			initializeStore();
		},
		[initializeStore]
	);
	useEffect(
		() => {
			if (loginId) {
				return;
			}
			pwrPasswordChangeInit(authKey, companyCode);
		},
		[loginId, pwrPasswordChangeInit]
	);

	const [newPassword, setNewPassword] = useState('');
	const [reInputPassword, setReInputPassword] = useState('');
	const handleChangeNewPassword = useCallback(
		createChangeHandler(setNewPassword),
		[createChangeHandler, setNewPassword]
	);
	const handleChangeReInputPassword = useCallback(
		createChangeHandler(setReInputPassword),
		[createChangeHandler, setReInputPassword]
	);

	const handleCloseMessageDialog = useCallback(
		createCloseMessageDialogHandler(showMessage),
		[showMessage]
	);

	const submit = useCallback(
		() => {
			const messages = verifyPassword(newPassword, reInputPassword);

			if (messages.length > 0) {
				showMessage(messages);
				return;
			}

			changePassword(authKey, newPassword, companyCode);
		},
		[
			verifyPassword,
			newPassword,
			reInputPassword,
			showMessage,
			changePassword,
			authKey,
		]
	);

	const handleKeyPress = useCallback(createKeyPressHandler(submit), [
		createKeyPressHandler,
		submit,
	]);

	return (
		<div className={classes.root}>
			<Progress open={isShowProgress} />
			<AppBar className={classes.appBar}>
				<Toolbar>
					<Typography variant="h6" color="inherit">
						{i18n.t('PwrPasswordChange_0008')}
					</Typography>
				</Toolbar>
			</AppBar>
			<Paper>
				<div className={classes.form}>
					<div>
						<Typography>
							{i18n.t('PwrPasswordChange_0009')}
						</Typography>
					</div>
					<form noValidate={true} autoComplete="off">
						<Grid
							container={true}
							spacing={2}
							direction="column"
							justify="center"
						>
							<Grid item={true}>
								<TextField
									type="text"
									name="loginId"
									label={i18n.t('PwrPasswordChange_0010')}
									value={pwr.loginID}
									disabled={true}
								/>
							</Grid>
							<Grid item={true}>
								<TextField
									type="password"
									name="changedPassword"
									label={i18n.t('PwrPasswordChange_0011')}
									value={newPassword}
									onChange={handleChangeNewPassword}
									onKeyPress={handleKeyPress}
								/>
							</Grid>
							<Grid item={true}>
								<TextField
									type="password"
									name="reInputPassword"
									label={i18n.t('PwrPasswordChange_0012')}
									value={reInputPassword}
									onChange={handleChangeReInputPassword}
									onKeyPress={handleKeyPress}
								/>
							</Grid>
							<Grid item={true}>
								<Grid
									container={true}
									spacing={3}
									justify="space-between"
								>
									<Grid item={true} xs={true}>
										<Button
											variant="contained"
											color="primary"
											onClick={submit}
										>
											{i18n.t('PwrPasswordChange_0013')}
										</Button>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</form>
				</div>
			</Paper>
			<MessageDialog
				open={message.open}
				contents={message.messages}
				title={message.title}
				showCancelButton={false}
				onClickOk={handleCloseMessageDialog}
				onClose={handleCloseMessageDialog}
				messageNs="loginmessage"
			/>
		</div>
	);
};

const mapStateToProps = (state: IRootState) => ({
	/** */
	isShowProgress: state.login.isShowProgress,
	/** */
	loginId: state.login.pwr.loginID,
	/** */
	loginState: state.login,
	/** */
	message: state.message,
	/** */
	profile: state.profile,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	/** */
	changePassword: (
		authKey: string,
		changedPassword: string,
		companyCode?: string
	) =>
		scenario.pwrPasswordChange(
			dispatch,
			authKey,
			changedPassword,
			companyCode
		),
	/** */
	initializeStore: () => {
		dispatch(initializeStoreAction({}));
	},
	/** */
	pushHistory: (path: string) => {
		dispatch(push(path));
	},
	/** */
	pwrPasswordChangeInit: (authKey: string, companyCode?: string) =>
		scenario.pwrPasswordChangeInit(dispatch, authKey, companyCode),
	showMessage: (messages: IMessage[]) => {
		dispatch(
			showMessageAction({
				messages,
				title: i18n.t('PwrPasswordChange_0014'),
			})
		);
	},
});

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(PwrChangePassword);



// WEBPACK FOOTER //
// ./src/containers/PwrPasswordChange.tsx